import React, { forwardRef } from 'react';
import _merge from 'lodash/merge';
import { useCombinedRefs, getContext, isAnimationDisabled } from 'common';
import SliderTrack from './SliderTrack';
import Styled from './styles';

const SliderContext = React.createContext<any>({});

export interface SliderProps extends React.ComponentPropsWithRef<'div'> {
  /** If true, the Slider will be aligned vertically. */
  vertical?: boolean;
  /** To render the min label, consider using component SliderLabel. */
  renderMinLabel?(...args: unknown[]): unknown;
  /** To render the max label, consider using component SliderLabel. */
  renderMaxLabel?(...args: unknown[]): unknown;
  /**
   * The min value of the slider. This will appear on left / right side of the slider based on RTL if no custom icon is defined.
   */
  min?: number;
  /**
   * The max value of the slider. This will appear on left / right side of the slider based on RTL if no custom icon is defined.
   */
  max?: number;
  /**
   * Amount that the value of the input will increment by. By default this is set to 1.
   */
  stepBy?: number;
  /** If true, slider will act like ticker. */
  isTicker?: boolean;
  /**
   * Callback function to capture the value of a slider. Returns object of shape: ({event, value}):
   *
   * `event`: SyntheticEvent.nativeEvent.
   *
   * `value`: new value of the slider.
   */
  onChange?(...args: unknown[]): unknown;
  /**
   * The value of the Slider, the value must be between or equal to max and min.
   */
  value: number | number[];
  /**
   * If true, the slider won't be functional, it will be greyed out, and there will be no pointer when hovering.
   */
  disabled?: boolean;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /**
   * Props only for track.
   * User could also pass custom props into it. Other props rather than these two will be passed to the wrapper of front and rear track.
   *
   * `frontTrackProps`: Props which be forwarded to wrapper element of the track between thumb and min label.
   *
   * `rearTrackProps`: Props which be forwarded to wrapper element of the track between thumb and max label.
   */
  trackProps?: {
    frontTrackProps?: {},
    rearTrackProps?: {}
  };
  /**
   * Props only for first thumb.
   * User could also pass custom props into it.  Other props will be passed to thumbContent and teardropContent functions.
   *
   * `teardropContent`: Custom teardrop component.
   *
   * `wrapperProps`: Props which will be passed to thumb wrapper element,
   *
   * `thumbContent`: Custom thumb component.
   *
   * `flipIndicatorPosition`: If true, the thumb indicator and tearDrop will be placed on the other side of the track.
   *
   * `alwaysShowTeardrop`: If true, tearDrop will stay visible when slider is ticker mode.
   *
   * `minThumbProps`: properties for the 1st Thumb if using a range, or the only Thumb if no range. Useful for screen reader properties.
   *
   * `maxThumbProps`: properties for the 2nd Thumb if using a range. Useful for screen reader properties.
   */
  thumbProps?: {
    teardropContent?(...args: unknown[]): unknown,
    thumbContent?(...args: unknown[]): unknown,
    wrapperProps?: {},
    flipIndicatorPosition?: boolean,
    alwaysShowTeardrop?: boolean,
    minThumbProps?: {},
    maxThumbProps?: {}
  };
  /** DOM attributes applied to the hidden input element. Field validation is controlled by you. Note: DOM attributes should be camelCased. */
  inputProps?: Partial<React.ComponentPropsWithRef<'input'>>;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}

/** Uncontrolled Slider.
 * All additional props will be forwarded to the root element.
 * To pass props to the root element of track and thumb, make use of the `trackProps` and `thumbProps` props.
 * To get the ref of the outermost element use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference.
 */
const Slider = forwardRef<HTMLElement, SliderProps>((props: SliderProps, ref) => getContext(({ RTL, theme, disableAnimation }) => {
  const sliderRef = React.createRef();
  const combinedRef = useCombinedRefs(ref, sliderRef);
  const {
    disabled,
    disableAnimation: propsdisableAnimation,
    vertical,
    isTicker,
    stepBy,
    theme: themeProp,
    min,
    max,
    onChange,
    value,
    renderMinLabel,
    renderMaxLabel,
    trackProps: passedTrackProps,
    thumbProps,
    inputProps,
    ...otherProps
  } = props;

  const resolvedTheme = _merge({}, theme.SLIDER, themeProp);

  const trackProps = {
    isTicker,
    vertical,
    leftValue: vertical ? min : !RTL ? min : max,
    rightValue: vertical ? max : !RTL ? max : min,
    stepBy,
    onChange,
    disabled,
    value,
    RTL,
    theme: themeProp,
    thumbProps,
    trackProps: passedTrackProps
  };

  const minLabelContent = renderMinLabel ? renderMinLabel() : min;
  const maxLabelContent = renderMaxLabel ? renderMaxLabel() : max;

  const resolvedLabelProps = {
    disabled, vertical, $rtl: RTL, $theme: resolvedTheme
  };
  return (
    <SliderContext.Provider value={{ disabled, disableAnimation: isAnimationDisabled(propsdisableAnimation, disableAnimation) }}>
      <Styled.Wrapper ref={combinedRef} vertical={vertical} RTL={RTL} {...otherProps}>
        <Styled.Label {...resolvedLabelProps}>{vertical ? maxLabelContent : minLabelContent}</Styled.Label>
        <SliderTrack {...trackProps} />
        <Styled.Label {...resolvedLabelProps}>{vertical ? minLabelContent : maxLabelContent}</Styled.Label>
      </Styled.Wrapper>
      <Styled.HiddenInput aria-hidden tabIndex={-1} readOnly {...inputProps} type="hidden" />
    </SliderContext.Provider>
  );
}));

Slider.defaultProps = {
  vertical: false,
  renderMinLabel: undefined,
  renderMaxLabel: undefined,
  disabled: false,
  min: 0,
  max: 10,
  stepBy: 1,
  isTicker: false,
  onChange: undefined,
  trackProps: {},
  thumbProps: {},
  inputProps: {},
  theme: {},
  disableAnimation: undefined,
};

Slider.displayName = 'Slider';
export { Slider as default, SliderContext };
